<script setup>
import { computed } from 'vue';
import { formatSignedPercent, signClass, toNumber } from '../format';
import { dayChange } from '../heatmap';
import { intlLocale, t } from '../i18n';
import { PANEL } from '../ui';

// «Котировки» above the heatmap (docs/quotes/SPEC.md): value and day change, polled with the heatmap.
// On desktop a narrow framed block like the payout calendar, not a full-width transparent one (decision 9).
const props = defineProps({
  quotes: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

// A row opens the instrument screen with the chart and news (docs/instrument/SPEC.md).
const emit = defineEmits(['open']);

const SUFFIX = { RUB: ' ₽', USD: ' $' };

function formatValue(quote) {
  const n = toNumber(quote.price);
  if (n === null) {
    return '—';
  }
  const text = new Intl.NumberFormat(intlLocale(), {
    minimumFractionDigits: quote.decimals,
    maximumFractionDigits: quote.decimals,
  }).format(n);
  return text + (SUFFIX[quote.currency] ?? '');
}

const rows = computed(() => props.quotes.map((quote) => {
  const change = dayChange(quote.price, quote.prev_close);
  // Only % with two digits (decision 5); a change that rounds to zero is grey.
  const shown = change === null ? null : Number(change.toFixed(2));
  return {
    key: quote.key,
    uid: quote.uid,
    name: t(`quotes.name.${quote.key}`),
    value: formatValue(quote),
    change: formatSignedPercent(shown, 2),
    changeClass: signClass(shown),
  };
}));
</script>

<template>
  <section
    :class="[PANEL, 'sm:max-w-md']"
    :aria-busy="loading ? 'true' : undefined"
  >
    <h2 class="text-[1.0625rem] font-semibold text-slate-100 sm:text-lg">
      {{ t('quotes.title') }}
    </h2>
    <div
      v-if="loading && rows.length === 0"
      class="mt-3 space-y-2"
    >
      <div
        v-for="n in 7"
        :key="n"
        class="h-9 rounded bg-slate-800 motion-safe:animate-pulse"
      />
    </div>
    <ul
      v-else
      class="mt-2 divide-y divide-slate-800"
    >
      <li
        v-for="row in rows"
        :key="row.key"
      >
        <button
          type="button"
          class="-mx-2 flex min-h-11 w-[calc(100%+1rem)] items-center justify-between gap-3 rounded-lg px-2 text-left text-[0.9375rem] hover:bg-slate-800/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 active:bg-slate-800"
          @click="emit('open', row.uid)"
        >
          <span class="min-w-0 truncate text-slate-100">{{ row.name }}</span>
          <span class="flex shrink-0 items-baseline gap-3 tabular-nums">
            <span class="text-slate-100">{{ row.value }}</span>
            <span
              class="w-20 text-right"
              :class="row.changeClass"
            >{{ row.change }}</span>
          </span>
        </button>
      </li>
    </ul>
  </section>
</template>
